import { useMemo, useState } from 'react';
import { useMediaQuery, Typography, Box, Button, Grid } from '@mui/material';
import { Check } from '@mui/icons-material';
import LoadingLogoSkeleton from '../../SkeletonLoading/loadingLogoSkeleton';
import PlanToggle from './PlanToggle';
import { computePricing, getPlanBullets, formatAmount } from './planCardUtils';

const useStyles = ({ isXS }) => ({
  container: {
    width: '100%',
    // maxWidth: '900px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingBottom: isXS ? '32px' : '48px',
    // backgroundColor: '#FFFFFF',
    borderRadius: isXS ? '0px' : '15px',
    px: isXS ? '0px' : '50px',
    // border: isXS ? 'none' : '1px solid #E1E6EF',
  },
  textHeadline: {
    fontWeight: 800,
    fontSize: { xxs: '20px', sm: '26px' },
    color: '#232B35',
  },
  blackPrice: {
    color: '#232B35',
    fontWeight: 800,
    fontSize: { xxs: '28px', sm: '30px' },
    mb: '5px',
  },
  priceLabel: {
    fontWeight: 400,
    fontSize: '10px',
    color: '#232B35',
    justifyContent: 'center',
    textAlign: 'center',
  },
  priceNote: {
    fontWeight: 700,
    fontSize: '10px',
    color: '#3171F6',
    justifyContent: 'center',
    textAlign: 'center',
    mb: '8px',
    mt: '-4px',
  },
  colHeader: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    // gap: { xxs: '8px', sm: '10px' },
    pb: { xxs: '4px', sm: '6px' },
    flexGrow: 1,
    position: 'relative',
    minHeight: { xxs: '120px', sm: '130px' },
  },
  colBorder: {
    border: '1px solid #C7D0DE',
    borderRadius: '16px',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 8px 20px rgba(21, 108, 254, 0.08)',
    width: '290px',
    minHeight: { xxs: '240px', sm: '260px' },
    padding: { xxs: '32px 18px 20px', sm: '36px 20px 24px' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: { xxs: '12px', sm: '16px' },
    position: 'relative',
  },
  upgradeButton: {
    borderRadius: '30px',
    width: '200px',
    height: '40px',
    fontWeight: 700,
    fontSize: '14px',
    padding: 0,
    background: '#3171F6',
    textTransform: 'none',
    color: '#FFFFFF',
    '&:hover': {
      background: '#3171F6',
    },
    '&.Mui-disabled': {
      background: '#D1D9E8',
      boxShadow: 'none',
      color: '#f4f6fb',
    },
  },
  bulletRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    width: '100%',
    color: '#232B35',
    fontSize: '13px',
    fontWeight: 400,
    textAlign: 'left',
    letterSpacing: '-0.43px',
  },
  checkIcon: {
    color: '#000000',
    fontSize: { xxs: '18px', sm: '20px' },
    mt: '2px',
  },
});

/**
 * Mapea el boardsCanJoinCount a la etiqueta solicitada en el ticket.
 */
function computeAnnualOnlyPricing(plan, subtitle) {
  const annual = plan?.recurlyPlanCodePrice ?? 0;
  return {
    line1Main: `$${formatAmount(annual)}`,
    line1Suffix: '/year',
    line2: subtitle ?? 'Turn it into a business expense',
  };
}

export default function Step1({
  onContinue,
  onOptionChange,
  onClose,
  // Debes pasar estos seis objetos: los 3 mensuales y los 3 anuales _en orden_ (1=Network, 2=Professional, 3=VIP)
  month1,
  month2,
  month3,
  annual1,
  annual2,
  annual3,
}) {
  const isXS = useMediaQuery((theme) => theme.breakpoints.down('xs'));
  const isLess900 = useMediaQuery((theme) => theme.breakpoints.down(900));
  const classes = useStyles({ isXS });
  // Ticket: default a ANUAL
  const [annualToggle, setAnnualToggle] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  // Definición de las 3 columnas según el ticket (plancodes freemium 1/2/3)
  const columns = useMemo(
    () => [
      { monthly: month1, annual: annual1, option: 1, lockedToAnnual: false },
      {
        monthly: month2,
        annual: annual2,
        option: 2,
        lockedToAnnual: false,
        tag: 'Most popular!',
      },
      {
        monthly: month3,
        annual: annual3,
        option: 3,
        lockedToAnnual: true,
        subtitle: 'Turn it into a business expense',
      },
    ],
    [month1, month2, month3, annual1, annual2, annual3]
  );

  return (
    <Box
      sx={{
        ...classes.container,
        mt: { xxs: '20px', sm: 0 },
      }}
    >
      <Typography
        sx={{
          color: '#151F33',
          textAlign: 'center',
          fontSize: { xxs: '28px', sm: '32px' },
          fontWeight: 800,
          lineHeight: { xxs: '32px', sm: '36px' },
          letterSpacing: '-0.43px',
          maxWidth: 'calc(100% - 90px)',
          mt: '20px',
        }}
      >
        Upgrade today for full access
      </Typography>
      {/* <PlanToggle
        value={annualToggle}
        setter={setAnnualToggle}
        label="Go annual and save!"
        containerProps={{
          sx: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: '20px',
          },
        }}
      /> */}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xxs: 'column', xs: 'row' },
          justifyContent: { xxs: 'center', sm: 'space-between' },
          flexWrap: 'wrap',
          width: '100%',
        }}
      >
        <Box
          sx={{
            mt: 2,
            width: '100%',
            textAlign: 'center',
            zIndex: 2,
          }}
        >
          <Grid container spacing={1} justifyContent="center" alignItems="stretch">
            {/* Tres columnas: Network / Professional / VIP */}
            {columns.map((col, idx) => {
              const isAnnualLocked = Boolean(col.lockedToAnnual);
              const effectiveToggle = isAnnualLocked ? true : annualToggle;
              const applicable = effectiveToggle ? col.annual : col.monthly;
              const tier = applicable?.salesforceValues?.tier;
              const price = isAnnualLocked
                ? computeAnnualOnlyPricing(col.annual, col.subtitle)
                : computePricing(col.monthly, col.annual, annualToggle);
              const bullets = getPlanBullets(
                col.option,
                applicable?.boardsCanJoinCount
              );

              return (
                <Grid
                  key={idx}
                  item
                  xs={isLess900 ? (isXS ? 12 : 6) : 4}
                  sm={4}
                  md={4}
                  display="flex"
                  justifyContent="center"
                >
                  <Box sx={classes.colBorder}>
                    {col.tag ? (
                      <Typography
                        sx={{
                          display: 'inline-block',
                          backgroundColor: '#3171F6',
                          color: '#FFFFFF',
                          px: '18px',
                          py: '2px',
                          borderRadius: '8px',
                          fontWeight: 700,
                          fontSize: { xxs: '10px', sm: '12px' },
                          position: 'absolute',
                          top: '8px',
                          right: '10px',
                          // transform: 'translateX(50%)',
                        }}
                      >
                        {col.tag}
                      </Typography>
                    ) : (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xxs: '12px', sm: '16px' },
                          height: { xxs: '22px', sm: '24px' },
                        }}
                      />
                    )}
                    <Box sx={classes.colHeader}>
                      <Typography sx={classes.textHeadline}>{tier}</Typography>
                      <Typography sx={classes.blackPrice}>
                        {price.line1Main}{' '}
                        <Typography component="span" sx={classes.priceLabel}>
                          {price.line1Suffix}
                        </Typography>
                      </Typography>
{/* 
                      <Typography sx={classes.priceNote}>
                        {price.line2}
                      </Typography> */}

                      <Button
                        className="ftUpgradeCTA"
                        variant="contained"
                        size="small"
                        sx={classes.upgradeButton}
                        onClick={() => {
                          setSelectedPlan(applicable);
                          onOptionChange?.(col, applicable, effectiveToggle);
                          onContinue?.(applicable);
                        }}
                        disabled={loading}
                        disableRipple
                      >
                        {loading &&
                        selectedPlan?.recurlyPlanCode ===
                          applicable?.recurlyPlanCode ? (
                          <LoadingLogoSkeleton
                            width={{ xxs: '20px', sm: '28px' }}
                            height={{ xxs: '20px', sm: '28px' }}
                          />
                        ) : (
                          'Upgrade'
                        )}
                      </Button>

                      <Box
                        component="ul"
                        sx={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          width: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          mt: { xxs: '8px', sm: '12px' },
                        }}
                      >
                        {bullets.map((content, bulletIdx) => (
                          <Typography
                            component="li"
                            key={bulletIdx}
                            sx={classes.bulletRow}
                          >
                            <Check sx={classes.checkIcon} />
                            <span>{content}</span>
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>

      {onClose && (
        <Typography
          role="button"
          tabIndex={0}
          onClick={() => onClose?.()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClose?.();
            }
          }}
          sx={{
            color: '#151F33',
            fontSize: { xxs: '13px', sm: '14px' },
            fontWeight: 600,
            textDecoration: 'underline',
            cursor: 'pointer',
            mt: '12px',
          }}
        >
          No thanks, maybe later
        </Typography>
      )}
    </Box>
  );
}
